import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import {
	getForms,
	createForm,
	deleteForm,
	clearCurrentForm,
} from '../../store/formBuilder';
import Card from '../../components/ui/card';
import Button from '../../components/ui/button';
import Input from '../../components/ui/input';
import Label from '../../components/ui/label';
import { Plus, Edit2, Trash2, Copy, Eye, MoreVertical, Calendar } from 'lucide-react';
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { cn } from '../../lib/utils';
import { format } from 'date-fns';

// Curated dark palette for form card fallbacks — sophisticated hues that stay within brand territory
const FALLBACK_GRADIENTS = [
	'from-[#1a1a2e] to-[#16213e]', // deep navy
	'from-[#2d1b2e] to-[#1a1225]', // deep plum
	'from-[#1a2820] to-[#0f1f18]', // dark forest
	'from-[#1e1e1e] to-[#111111]', // charcoal black
	'from-[#0f1a2e] to-[#0a1220]', // midnight blue
	'from-[#2a1f1f] to-[#1a1212]', // dark burgundy
];

// Deterministic color from form ID so it's stable across sessions
const getFallbackGradient = (formId) => {
	const index = (typeof formId === 'number' ? formId : 0) % FALLBACK_GRADIENTS.length;
	return FALLBACK_GRADIENTS[index];
};

const FormBuilderList = () => {
	const dispatch = useDispatch();
	const history = useHistory();
	const { forms, status, error, apiNotAvailable } = useSelector(
		(state) => state.formBuilder
	);
	const token = useSelector((state) => state.admin.token);
	const [showDeleteModal, setShowDeleteModal] = useState(false);
	const [formToDelete, setFormToDelete] = useState(null);
	const [searchTerm, setSearchTerm] = useState('');
	const [selectedCategory, setSelectedCategory] = useState('all');
	const [sortBy, setSortBy] = useState('name'); // name, date, category
	const [sortOrder, setSortOrder] = useState('asc'); // asc, desc

	useEffect(() => {
		// Fetch forms when component mounts
		dispatch(getForms({ token }));

		// Clear current form when returning to list view
		dispatch(clearCurrentForm());
	}, [dispatch, token]);

	// Restore scroll position after forms have loaded and rendered
	useEffect(() => {
		// Only restore scroll if we have forms and they've finished loading
		if (status === 'succeeded' && forms.length > 0) {
			const savedScrollPosition = sessionStorage.getItem('formBuilderScrollPosition');
			if (savedScrollPosition) {
				// Use requestAnimationFrame to ensure DOM has painted
				requestAnimationFrame(() => {
					setTimeout(() => {
						window.scrollTo(0, parseInt(savedScrollPosition, 10));
						sessionStorage.removeItem('formBuilderScrollPosition');
					}, 50);
				});
			}
		}
	}, [status, forms.length]); // Run when loading completes

	const handleCreateForm = () => {
		// Save scroll position before navigating
		sessionStorage.setItem('formBuilderScrollPosition', window.scrollY.toString());
		history.push('/admin/form-builder/create');
	};

	const handleEditForm = (id) => {
		// Save scroll position before navigating
		sessionStorage.setItem('formBuilderScrollPosition', window.scrollY.toString());
		history.push(`/admin/form-builder/edit/${id}`);
	};

	const handleViewForm = (id) => {
		// Save scroll position before navigating
		sessionStorage.setItem('formBuilderScrollPosition', window.scrollY.toString());
		history.push(`/admin/form-builder/view/${id}`);
	};

	const handleDuplicateForm = async (form) => {
		try {
			// Create a copy of the form with a new name
			const duplicateData = {
				name: `${form.name} (Copy)`,
				description: form.description,
				category: form.category,
				is_active: false, // Set to inactive by default
			};

			await dispatch(createForm({ formData: duplicateData, token }));
			// Refresh the forms list
			dispatch(getForms({ token }));
		} catch (error) {
			console.error('Error duplicating form:', error);
		}
	};

	const confirmDelete = (form) => {
		setFormToDelete(form);
		setShowDeleteModal(true);
	};

	const handleDeleteForm = async () => {
		if (formToDelete) {
			await dispatch(deleteForm({ id: formToDelete.id, token }));
			setShowDeleteModal(false);
			setFormToDelete(null);
		}
	};

	const cancelDelete = () => {
		setShowDeleteModal(false);
		setFormToDelete(null);
	};

	// Render delete confirmation modal
	const DeleteConfirmationModal = () => (
		<div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50'>
			<div className='bg-white p-8 rounded-sm shadow-2xl max-w-md w-full border border-gray-100'>
				<h2 className='text-xl font-glacial text-clientBlack mb-4'>Delete Form</h2>
				<p className='mb-6'>
					Are you sure you want to delete the form "{formToDelete?.name}"? This
					action cannot be undone.
				</p>
				<div className='flex justify-end space-x-2'>
					<Button variant='outline' onClick={cancelDelete}>
						Cancel
					</Button>
					<Button variant='destructive' onClick={handleDeleteForm}>
						Delete
					</Button>
				</div>
			</div>
		</div>
	);

	// Get unique categories from forms
	const categories = ['all', ...new Set(forms.map((form) => form.category))];

	// Filter and sort forms
	const filteredForms = forms
		.filter((form) => {
			const matchesSearch =
				form.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
				form.description.toLowerCase().includes(searchTerm.toLowerCase());
			const matchesCategory =
				selectedCategory === 'all' || form.category === selectedCategory;
			return matchesSearch && matchesCategory;
		})
		.sort((a, b) => {
			let comparison = 0;
			switch (sortBy) {
				case 'name':
					comparison = a.name.localeCompare(b.name);
					break;
				case 'date':
					comparison = new Date(b.updated_at) - new Date(a.updated_at);
					break;
				case 'category':
					comparison = a.category.localeCompare(b.category);
					break;
				default:
					comparison = 0;
			}
			return sortOrder === 'asc' ? comparison : -comparison;
		});

	const handleSort = (field) => {
		if (sortBy === field) {
			setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
		} else {
			setSortBy(field);
			setSortOrder('asc');
		}
	};

	const renderSortIcon = (field) => {
		if (sortBy !== field) return null;
		return sortOrder === 'asc' ? '↑' : '↓';
	};

	// Display loading state - modified to handle the error case properly
	if (status === 'loading' && forms.length === 0) {
		return (
			<div className='flex flex-col justify-center items-center h-64 gap-4'>
				<div className='animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary'></div>
				<p className='text-xs font-montserrat-medium uppercase tracking-widest text-gray-400'>Loading Forms</p>
			</div>
		);
	}

	// Add a condition to handle the failed state with fallback UI
	if (status === 'failed' && forms.length === 0) {
		return (
			<div className='container mx-auto px-4 sm:px-6 py-8 sm:py-10'>
				<div className='flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6'>
					<h1 className='text-xl sm:text-2xl font-glacial uppercase tracking-wider text-clientBlack'>Form Builder</h1>
					<Button onClick={handleCreateForm} className='flex items-center gap-1.5 font-montserrat-medium uppercase tracking-wider text-sm px-5 whitespace-nowrap'>
						<Plus className='w-4 h-4' />
						Create New Form
					</Button>
				</div>

				{error && (
					<div className='bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4'>
						{error}
					</div>
				)}

				<Card>
					<Card.Header>
						<div className='text-xl font-semibold'>Forms</div>
					</Card.Header>
					<Card.Content>
						<div className='flex flex-col items-center justify-center py-12'>
							<p className='text-gray-500 mb-4'>
								{apiNotAvailable
									? "The Forms API endpoint is not available. You can still create forms but they won't be saved to the server."
									: 'No forms available. Try creating a new form.'}
							</p>
							<Button onClick={handleCreateForm}>Create Your First Form</Button>
						</div>
					</Card.Content>
				</Card>
			</div>
		);
	}

	// Helper to format the date safely
	const formatDate = (dateStr) => {
		try {
			if (dateStr) {
				const date = new Date(dateStr);
				if (isNaN(date.getTime())) return 'Invalid date';
				return format(date, 'MMM d, yyyy');
			}
			return 'Unknown';
		} catch (err) {
			console.warn('Error formatting date:', err);
			return 'Invalid date';
		}
	};

	return (
		<div className='container mx-auto px-4 sm:px-6 py-8 sm:py-10'>
			{showDeleteModal && <DeleteConfirmationModal />}

			{/* Page header — stacks on mobile/tablet, inline on lg+ */}
			<div className='flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 mb-8'>
				<h1 className='text-xl md:text-2xl font-glacial uppercase tracking-wider text-clientBlack'>Form Builder</h1>
				<Button onClick={handleCreateForm} className='flex items-center gap-1.5 font-montserrat-medium uppercase tracking-wider text-sm px-5 whitespace-nowrap'>
					<Plus className='w-4 h-4' />
					Create New Form
				</Button>
			</div>

			{error && (
				<div className='bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-sm mb-4'>
					{error}
				</div>
			)}

			{/* Filter controls */}
			<Card className='mb-8'>
				<div className='p-4'>
					<div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4'>
						<div>
							<Label htmlFor='search'>Search Forms</Label>
							<Input
								id='search'
								type='search'
								placeholder='Search by name or description'
								value={searchTerm}
								onChange={(e) => setSearchTerm(e.target.value)}
							/>
						</div>
						<div>
							<Label htmlFor='category'>Category</Label>
							<select
								id='category'
								value={selectedCategory}
								onChange={(e) => setSelectedCategory(e.target.value)}
								className='w-full border border-gray-300 rounded-sm px-3 py-2 text-sm font-montserrat-regular text-gray-700 focus:outline-none focus:ring-1 focus:ring-primary/40 focus:border-primary transition-colors duration-200 bg-white'>
								{categories.map((category) => (
									<option key={category} value={category}>
										{category.charAt(0).toUpperCase() + category.slice(1)}
									</option>
								))}
							</select>
						</div>
						<div className='sm:col-span-2 lg:col-span-1'>
							<Label>Sort By</Label>
							<div className='flex space-x-2'>
								<Button
									variant={sortBy === 'name' ? 'accent' : 'outline'}
									size='sm'
									onClick={() => handleSort('name')}
									aria-pressed={sortBy === 'name'}
									aria-label={`Sort by name ${sortBy === 'name' ? (sortOrder === 'asc' ? 'ascending' : 'descending') : ''}`}
									className='flex-1'>
									Name {renderSortIcon('name')}
								</Button>
								<Button
									variant={sortBy === 'date' ? 'accent' : 'outline'}
									size='sm'
									onClick={() => handleSort('date')}
									aria-pressed={sortBy === 'date'}
									aria-label={`Sort by date ${sortBy === 'date' ? (sortOrder === 'asc' ? 'ascending' : 'descending') : ''}`}
									className='flex-1'>
									Date {renderSortIcon('date')}
								</Button>
								<Button
									variant={sortBy === 'category' ? 'accent' : 'outline'}
									size='sm'
									onClick={() => handleSort('category')}
									aria-pressed={sortBy === 'category'}
									aria-label={`Sort by category ${sortBy === 'category' ? (sortOrder === 'asc' ? 'ascending' : 'descending') : ''}`}
									className='flex-1'>
									Category {renderSortIcon('category')}
								</Button>
							</div>
						</div>
					</div>
				</div>
			</Card>

			{/* Form cards grid — 1 col mobile, 2 col tablet, 3 col desktop */}
			<div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6'>
				{filteredForms.map((form) => {
					const headerImage = form.headerImage || form.header_image || '';
					const fallbackGradient = getFallbackGradient(form.id);

					return (
						<Card
							key={form.id}
							className='relative cursor-pointer group overflow-hidden transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5 flex flex-col focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2'
							onClick={() => handleEditForm(form.id)}
							role='button'
							tabIndex={0}
							aria-label={`Edit form: ${form.name}`}
							onKeyDown={(e) => {
								// Allow keyboard events unless from within dropdown menu
								if (e.target.closest('[role="menu"]')) return;
								if (e.key === 'Enter' || e.key === ' ') {
									e.preventDefault();
									handleEditForm(form.id);
								}
							}}>
							{/* Header image banner */}
							{headerImage ? (
								<div className='relative w-full h-32 sm:h-36 overflow-hidden flex-shrink-0' aria-hidden='true'>
									<img
										src={headerImage}
										alt=''
										loading='lazy'
										className='w-full h-full object-cover transition-transform duration-500 group-hover:scale-105'
									/>
									<div className='absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent' />
									<div className='absolute bottom-0 left-0 right-0 h-0.5 bg-primary' />
								</div>
							) : (
								<div className={cn('relative w-full h-32 sm:h-36 overflow-hidden flex-shrink-0 bg-gradient-to-b flex items-center justify-center', fallbackGradient)} aria-hidden='true'>
									<span className='font-glacial text-white/20 text-5xl sm:text-6xl uppercase tracking-widest select-none'>
										{form.name.charAt(0)}
									</span>
									<div className='absolute bottom-0 left-0 right-0 h-0.5 bg-primary' />
								</div>
							)}

							{/* Dropdown menu — scrim ensures visibility on any background */}
							<div
								className='absolute top-3 right-3 z-10'
								onClick={(e) => e.stopPropagation()}>
								<DropdownMenu>
									<DropdownMenuTrigger asChild>
										<Button
											variant='ghost'
											size='sm'
											className='rounded-full bg-black/30 hover:bg-black/50 backdrop-blur-sm text-white'>
											<MoreVertical className='w-4 h-4' />
										</Button>
									</DropdownMenuTrigger>
									<DropdownMenuContent>
										<DropdownMenuItem onClick={() => handleEditForm(form.id)}>
											<Edit2 className='w-4 h-4 mr-2' />
											Edit
										</DropdownMenuItem>
										<DropdownMenuItem onClick={() => handleViewForm(form.id)}>
											<Eye className='w-4 h-4 mr-2' />
											Preview
										</DropdownMenuItem>
										<DropdownMenuItem onClick={() => handleDuplicateForm(form)}>
											<Copy className='w-4 h-4 mr-2' />
											Duplicate
										</DropdownMenuItem>
										<DropdownMenuItem
											className='text-red-500'
											onClick={() => confirmDelete(form)}>
											<Trash2 className='w-4 h-4 mr-2' />
											Delete
										</DropdownMenuItem>
									</DropdownMenuContent>
								</DropdownMenu>
							</div>

							{/* Card content */}
							<div className='p-4 flex flex-col flex-1'>
								<div className='mb-3'>
									<h3 className='text-base font-montserrat-medium text-clientBlack mb-1 pr-4 leading-normal'>{form.name}</h3>
									{form.description && (
										<p className='text-sm font-montserrat-regular text-gray-400 line-clamp-2'>
											{form.description}
										</p>
									)}
								</div>

								{/* Metadata footer */}
								<div className='flex items-center justify-between mt-auto pt-3 border-t border-gray-100 gap-2'>
									<div className='flex items-center gap-2 sm:gap-3 min-w-0'>
										<span className='text-[10px] sm:text-xs font-montserrat-regular text-gray-400 uppercase tracking-wide truncate'>
											{form.category}
										</span>
										<span
											role='status'
											aria-label={form.is_active ? 'Form is active' : 'Form is inactive'}
											className={cn(
												'inline-flex items-center gap-1 sm:gap-1.5 px-1.5 sm:px-2 py-0.5 rounded-sm text-[10px] sm:text-xs font-montserrat-medium border whitespace-nowrap',
												form.is_active
													? 'bg-green-50 text-green-700 border-green-200'
													: 'bg-gray-50 text-gray-500 border-gray-200'
											)}>
											<span
												className={cn(
													'w-1.5 h-1.5 rounded-full flex-shrink-0',
													form.is_active ? 'bg-green-500' : 'bg-gray-400'
												)}
												aria-hidden='true'
											/>
											{form.is_active ? 'Active' : 'Inactive'}
										</span>
									</div>
									<span className='flex items-center gap-1 text-[10px] sm:text-xs font-montserrat-regular text-gray-400 whitespace-nowrap flex-shrink-0'>
										<Calendar className='w-3 h-3 hidden sm:block' aria-hidden='true' />
										{formatDate(form.updated_at)}
									</span>
								</div>
							</div>
						</Card>
					);
				})}

				{filteredForms.length === 0 && (
					<div className='col-span-full'>
						<div className='flex flex-col items-center justify-center py-16 sm:py-20'>
							<div className='w-16 h-px bg-primary mx-auto mb-8' />
							<p className='font-glacial text-xl sm:text-2xl text-clientBlack mb-2'>No Forms Found</p>
							<p className='font-montserrat-regular text-sm text-gray-400 mb-6 text-center max-w-xs'>
								{searchTerm || selectedCategory !== 'all'
									? 'Try adjusting your search or clearing the category filter.'
									: 'Create your first form to start collecting client information.'}
							</p>
							{(searchTerm || selectedCategory !== 'all') && (
								<Button
									variant='outline'
									size='sm'
									className='mb-3'
									onClick={() => { setSearchTerm(''); setSelectedCategory('all'); }}>
									Clear Filters
								</Button>
							)}
							<Button onClick={handleCreateForm}>
								<Plus className='w-4 h-4 mr-2' />
								Create New Form
							</Button>
						</div>
					</div>
				)}
			</div>
		</div>
	);
};

export default FormBuilderList;
